import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useMemo, useRef } from "react";
import { Field as FinalField, Form as FinalForm, useForm } from "react-final-form";
import { FormattedMessage } from "react-intl";
// export for use in tests
export const codeFormTestId = "response-code-form";
export function ResponseCodeForm(props) {
    // Kept stable across renders on purpose. react-final-form compares initialValues shallowly, so a
    // freshly built object here counts as new initial values on every render of this component, and
    // re-initialising the form throws away the digits the user has entered. Anything that re-renders
    // the parent - showing a validation message, a countdown tick - would otherwise clear the code.
    const initialValues = useMemo(() => {
        const valueChars = (props.code && typeof props.code === "string" ? props.code : "").split("");
        return { v: [valueChars[0], valueChars[1], valueChars[2], valueChars[3], valueChars[4], valueChars[5]] };
    }, [props.code]);
    return (_jsx(FinalForm, { onSubmit: (values) => {
            props.handleSubmitCode(values);
        }, initialValues: initialValues, render: (formProps) => {
            // Add the formProps to all the children of this component. The children are typically buttons,
            // and they need to know some of the formProps to know if they should be disabled/readonly or not.
            const childrenWithProps = React.Children.map(props.children, (child) => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, { formProps });
                }
                return child;
            });
            return (_jsxs(Fragment, { children: [_jsx(ShortCodeForm, Object.assign({}, formProps, props)), childrenWithProps] }));
        } }));
}
function ShortCodeForm(props) {
    var _a;
    const showBadAttempts = Boolean(props.bad_attempts && props.bad_attempts > 0);
    const autoFocusCode = (_a = props.autoFocusCode) !== null && _a !== void 0 ? _a : true;
    return (_jsxs("form", { onSubmit: props.handleSubmit, className: "response-code-form", "data-testid": codeFormTestId, children: [props.extraFields, _jsxs("div", { className: "response-code-inputs", children: [_jsx("label", { htmlFor: "v[0]", className: "required", children: _jsx(FormattedMessage, { id: "common.code", defaultMessage: "Code", description: "email code" }) }), _jsx(CodeField, { num: 0, readonly: props.inputsDisabled, autoFocus: autoFocusCode && !props.inputsDisabled }), _jsx(CodeField, { num: 1, readonly: props.inputsDisabled }), _jsx(CodeField, { num: 2, readonly: props.inputsDisabled }), _jsx(CodeField, { num: 3, readonly: props.inputsDisabled }), _jsx(CodeField, { num: 4, readonly: props.inputsDisabled }), _jsx(CodeField, { num: 5, readonly: props.inputsDisabled })] }), showBadAttempts && (_jsx("div", { children: _jsx("span", { className: "input-validate-error", id: "wrong-code-error", children: _jsx(FormattedMessage, { id: "responseCodeForm.short", defaultMessage: "Incorrect code, try again", description: "Short code form" }) }) })), props.error && _jsx("p", { children: props.error })] }));
}
function validateCodeForm(value) {
    if (!value) {
        return "required";
    }
}
// helper-component to make for tidy code with one line per input field in ShortCodeForm
function CodeField({ num, value, autoFocus = undefined, readonly }) {
    const inputsRef = useRef([]);
    const form = useForm();
    function selectIfNotEmpty(event) {
        if (event.target.value) {
            event.target.select();
        }
    }
    function handleInput(event) {
        const target = event.target;
        const value = target.value;
        const onlyNumbers = value.replaceAll(/\D/g, ""); // Remove all non-numeric characters
        // Update the input field's value with only numeric characters
        target.value = onlyNumbers;
        if (onlyNumbers && target.nextElementSibling instanceof HTMLInputElement) {
            target.nextElementSibling.focus();
        }
    }
    function handleBackspace(event) {
        var _a;
        const target = event.target;
        if (event.key === "Backspace" &&
            (target === null || target === void 0 ? void 0 : target.value) === "" &&
            target.previousElementSibling instanceof HTMLInputElement) {
            (_a = target === null || target === void 0 ? void 0 : target.previousElementSibling) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }
    function handleArrows(event) {
        const target = event.target;
        if (event.key === "ArrowLeft" && target.previousElementSibling instanceof HTMLInputElement) {
            event.preventDefault();
            target.previousElementSibling.focus();
        }
        if (event.key === "ArrowRight" && target.nextElementSibling instanceof HTMLInputElement) {
            event.preventDefault();
            target.nextElementSibling.focus();
        }
        if ((form.getState().valid && event.key === "Enter") || event.keyCode === 13) {
            form.submit();
        }
    }
    function handlePaste(event) {
        var _a;
        event.preventDefault();
        const pastedText = event.clipboardData.getData("text");
        const numbersOnly = pastedText.replaceAll(/\D/g, "");
        const digits = numbersOnly.split("");
        // Get all the input elements
        const inputs = document.querySelectorAll('input[name^="v["]');
        // Save the current input values
        const currentValues = Array.from(inputs).map((input) => input.value);
        // Function to update input and form data
        const updateInputAndForm = (input, index, value) => {
            input.value = value;
            const fieldName = `v[${index}]`;
            form.change(fieldName, value);
        };
        // Distribute the digits to each input
        Array.from(inputs).forEach((input, i) => {
            const digit = digits[i];
            if (!input.value) {
                updateInputAndForm(input, i, digit);
            }
        });
        // Restore the previous input values for any remaining empty inputs
        Array.from(inputs)
            .slice(digits.length)
            .forEach((input, i) => {
            const newValue = currentValues[digits.length + i] || "";
            if (!input.value) {
                updateInputAndForm(input, digits.length + i, newValue);
            }
        });
        const cursorPosition = (_a = event.currentTarget.selectionStart) !== null && _a !== void 0 ? _a : 0;
        if (cursorPosition < inputs.length) {
            const remainingDigits = digits.slice(cursorPosition);
            Array.from(inputs)
                .slice(cursorPosition)
                .forEach((input, i) => {
                var _a;
                const digit = (_a = remainingDigits[i]) !== null && _a !== void 0 ? _a : "";
                updateInputAndForm(input, cursorPosition + i, digit);
            });
        }
    }
    return (_jsx(FinalField, { id: `v[${num}]`, name: `v[${num}]`, component: "input", type: "number", pattern: "[0-9]*", maxLength: "1", placeholder: value, autoFocus: autoFocus, validate: validateCodeForm, ref: (input) => {
            if (input)
                inputsRef.current.push(input);
            return undefined;
        }, onFocus: selectIfNotEmpty, onInput: handleInput, onKeyUp: handleBackspace, onKeyDown: handleArrows, onPaste: handlePaste, readOnly: readonly, "aria-label": `Enter digit in ${num}'s input` }));
}
